import { Component, OnInit, Renderer2, ViewChild, ElementRef } from '@angular/core';

import { environment } from '../../environments/environment';
import { UsersService } from './users.service';
import { FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { startWith, map, takeUntil } from 'rxjs/operators';
import { LoaderService } from '../loader/loader.service';
import * as moment from 'moment';
import { User } from './models/user';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionsService } from '../services/permissions.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit {
  env;
  user: User;
  users: User[];
  users$: Observable<User[]>;

  modalActive: NgbModalRef;

  timeoutIDError;

  page = 1;
  pageSize = 10;
  collectionSize = 0;
  filter = new FormControl('');

  private unsubscribe$ = new Subject();

  @ViewChild('alertError', { read: ElementRef, static: false })
  alertError: ElementRef;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public permissionsService: PermissionsService,
    private usersService: UsersService,
    private loaderService: LoaderService,
    private renderer: Renderer2,
    private modalService: NgbModal
  ) {
    this.env = environment;
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      if (this.permissionsService.isPermitted('users')) {
        this.getUsers(this.env.selected.cnpj);
      } else {
        this.router.navigateByUrl(`/dash/${params.cnpj}`);
      }
    });
  }

  addErrorAlert(msg: string) {
    clearTimeout(this.timeoutIDError);

    this.renderer.setProperty(this.alertError.nativeElement, 'innerHTML', msg);
    this.renderer.setProperty(this.alertError.nativeElement, 'hidden', false);

    this.timeoutIDError = setTimeout(_ => {
      this.renderer.setProperty(this.alertError.nativeElement, 'hidden', true);
    }, 8000);
  }

  async getUsers(cnpj: string) {
    const loader = await this.loaderService.notifica({
      title: 'Buscando usuários...',
      date: moment().toISOString(),
      type: 'loading',
    });

    await this.handlePermissionsToGetUsers(cnpj);

    this.users$ = this.filter.valueChanges.pipe(
      startWith(''),
      map(text => this.search(text))
    );

    loader.dismiss();
  }

  private async handlePermissionsToGetUsers(cnpj: string) {
    const users = await this.usersService.getUsers(cnpj).toPromise();

    if (!this.permissionsService.isProfile('Master')) {
      this.users = users.filter(user => user.novoPerfil.nome !== 'Master');
    } else {
      this.users = users;
    }
  }

  async changeUserStatus(cnpj: string, uid: string) {
    const loader = await this.loaderService.notifica({
      title: 'Atualizando usuário...',
      date: moment().toISOString(),
      type: 'loading',
    });

    const changedUser = await this.usersService.changeUserStatus(cnpj, uid).toPromise();

    if ('error' in changedUser) {
      this.addErrorAlert(changedUser.error);
    } else {
      this.getUsers(this.env.selected.cnpj);
    }

    this.modalActive.close();
    this.user = null;

    loader.dismiss();
  }

  openModalChangeUserStatus(user: User, modal) {
    if (!this.permissionsService.isProfile('Master')) {
      if (user.novoPerfil.nome === 'Master') {
        this.addErrorAlert('Seu usuário não tem permissão para editar usuário com perfil Master');
      } else {
        this.user = user;
        this.modalActive = this.modalService.open(modal, {
          size: 'lg',
          centered: true,
        });
      }
    } else {
      this.user = user;
      this.modalActive = this.modalService.open(modal, {
        size: 'lg',
        centered: true,
      });
    }
  }

  editUser(user: User) {
    if (!this.permissionsService.isProfile('Master')) {
      if (user.novoPerfil.nome === 'Master') {
        this.addErrorAlert('Seu usuário não tem permissão para editar usuário com perfil Master');
      } else {
        this.router.navigateByUrl(`/users/${this.env.selected.cnpj}/${user.uid}/edit`);
      }
    } else {
      this.router.navigateByUrl(`/users/${this.env.selected.cnpj}/${user.uid}/edit`);
    }
  }

  search(text: string): User[] {
    const usersFiltered: User[] = this.users.filter(user => {
      const term = text.toLowerCase();
      return (
        user.nome.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.novoPerfil.nome.toLowerCase().includes(term)
      );
    });

    this.page = 1;
    this.collectionSize = usersFiltered.length;

    return usersFiltered;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
